import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';

/**
 * Per-dispensary LaunchDarkly flag gating the embedded (iframe) concierge
 * entry points + fullscreen-in-iframe modals (ECOMM-12301).
 *
 * The embedded surface reuses the SAME `storeSettings.enableConcierge` admin
 * toggle as the storefront concierge (ECOMM-12299/12300) — there's no second
 * admin control. But the embedded-in-iframe experience isn't fully hardened
 * yet (fullscreen modal audit, mobile/dynamic-viewport correctness, embedded
 * checkout, a11y/analytics — the rest of the ECOMM-12298 epic), so it needs a
 * rollout switch that's independent of storefront: a dispensary can already
 * have `enableConcierge` on for its storefront while this flag stays off,
 * keeping embedded menus on today's (no entry points, no fullscreen modals)
 * behavior until we explicitly opt them in — and letting us kill embedded
 * without touching the storefront rollout.
 *
 * Read via `useDispensaryFlag` — the same mechanism as `ORDER_TYPE_TAKEOVER_FLAG`
 * — so it supports a plain boolean (global on/off) or a per-dispensary
 * `{ enabledForDispensaries: [...] }` / `{ enabledForAll: true }` JSON flag,
 * evaluated client-side against the dispensary id (not subject to the
 * SSR-bootstrap context race — ECOMM-11202).
 */
export const EMBEDDED_CONCIERGE_ACCESS_FLAG = 'growth.ecomm.embedded-concierge-access.rollout';

/**
 * Whether the embedded (iframe) concierge experience is rolled out for this
 * dispensary. Callers still need to AND this with the `enableConcierge` store
 * setting and `UI.isEmbedded` — this hook is only the flag half of the gate.
 */
export function useIsEmbeddedConciergeAccessEnabled(dispensaryId: string | null | undefined): boolean {
  return Boolean(useDispensaryFlag(EMBEDDED_CONCIERGE_ACCESS_FLAG, dispensaryId ?? undefined, false));
}
